import * as React from "react"
import { cn } from "@/lib/utils"

export interface EditImageIconProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Size of the icon in pixels
   * @default 18
   */
  size?: number
}

/**
 * EditImage icon as SVG (image with plus sign overlay)
 *
 * This icon represents the edit/modify image action in the ANYW3AR app,
 * typically used with avatar components to indicate editability.
 *
 * @example
 * ```tsx
 * <EditImageIcon />
 * <EditImageIcon size={8} className="text-gray-500" />
 * ```
 */
export const EditImageIcon = React.forwardRef<SVGSVGElement, EditImageIconProps>(
  ({ size = 18, className, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox="0 0 10.9 8.1"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        className={cn("inline-flex shrink-0", className)}
        aria-hidden="true"
        {...props}
      >
        <path d="M1.4,7.5c-.4,0-.8-.1-1-.4-.2-.2-.4-.6-.4-1V1.4C0,.9.1.6.4.4.6.1.9,0,1.4,0h6.6C8.4,0,8.7.1,9,.4c.2.2.4.6.4,1v2.1c0,0-.2,0-.2,0,0,0-.2,0-.3,0s-.2,0-.3,0c0,0-.2,0-.2,0V1.5c0-.2,0-.3-.1-.4,0,0-.2-.1-.4-.1H1.5c-.2,0-.3,0-.4.1,0,0-.1.2-.1.4v3.9l.9-.8c0,0,.2-.1.2-.2,0,0,.2,0,.3,0s.2,0,.3,0c0,0,.2,0,.3.2l.7.6,1.8-1.6c0,0,.2-.1.3-.2,0,0,.2,0,.3,0s.2,0,.3,0c.1,0,.2,0,.3.2l.5.4c-.3.2-.5.5-.6.9-.2.3-.2.7-.2,1.1s0,.5.1.7c0,.2.2.5.3.7H1.4ZM3.2,3.9c-.2,0-.3,0-.5-.1-.1,0-.3-.2-.4-.3,0-.1-.1-.3-.1-.5s0-.3.1-.5c0-.1.2-.3.4-.4.1,0,.3-.1.5-.1s.3,0,.5.1c.1,0,.3.2.3.4,0,.1.1.3.1.5s0,.3-.1.5c0,.1-.2.3-.3.3-.1,0-.3.1-.5.1ZM8.8,8.1c-.3,0-.5,0-.8-.2-.2-.1-.5-.3-.7-.4-.2-.2-.3-.4-.4-.7-.1-.2-.2-.5-.2-.8s0-.5.2-.8c.1-.2.3-.5.4-.7.2-.2.4-.3.7-.4.2-.1.5-.2.8-.2s.5,0,.8.2c.2.1.5.3.7.4.2.2.3.4.4.7.1.2.2.5.2.8s0,.5-.2.8c-.1.2-.3.5-.4.7-.2.2-.4.3-.7.4-.2.1-.5.2-.8.2ZM8.8,7.4c0,0,.2,0,.2,0,0,0,0-.1,0-.2v-.7h.7c0,0,.2,0,.2,0,0,0,0-.1,0-.2s0-.2,0-.2c0,0-.1,0-.2,0h-.7v-.7c0,0,0-.2,0-.2,0,0-.1,0-.2,0s-.2,0-.2,0c0,0,0,.1,0,.2v.7h-.7c0,0-.2,0-.2,0,0,0,0,.1,0,.2s0,.2,0,.2c0,0,.1,0,.2,0h.7v.7c0,0,0,.2,0,.2,0,0,.1,0,.2,0Z" />
      </svg>
    )
  }
)

EditImageIcon.displayName = "EditImageIcon"
